import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { toValue } from "vue";
import { useContentCommandApi } from "@/composables/useContentCommandApi";
import { useFeatureFlag } from "@/features/experience-shared/composables/useFeatureFlag";
import {
  useOperationalTranslationsQuery,
  OPERATIONAL_TRANSLATIONS_QUERY_KEY,
} from "@/features/experience-curation/queries/useOperationalTranslationsQuery";
import { createOperationalTitleField } from "@/features/experience-curation/stores/createOperationalTitleField";

/**
 * Mutation responsible for persisting all operational translation titles.
 * Iterates over dynamic operational title field stores and saves those with changes.
 */
export function useOperationalTranslationsSaveMutation() {
  const queryClient = useQueryClient();
  const { patchExperienceOperationalTranslation } = useContentCommandApi();
  const isEnabled = useFeatureFlag("enable_commercial_shared_factsheet");
  const query = useOperationalTranslationsQuery("en");

  return useMutation({
    mutationFn: async () => {
      if (!isEnabled) return;

      const translations = toValue(query.data) ?? [];

      const saves = translations
        .map((translation) => ({
          translationId: translation.id,
          fieldStore: createOperationalTitleField(translation.operational_id)(),
        }))
        .filter(({ fieldStore }) => fieldStore.hasChanges)
        .map(({ translationId, fieldStore }) =>
          patchExperienceOperationalTranslation(translationId, { name: fieldStore.model })
        );

      await Promise.all(saves);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [OPERATIONAL_TRANSLATIONS_QUERY_KEY] });
    },
  });
}
